import { DirectiveOptions } from 'vue'

interface ColumnResizeOptions {
  minWidth?: number;
  maxWidth?: number;
  onResize?: (width: number, columnId: string) => void;
  storageKey?: string;
  columnId?: string;
}

export const columnResize: DirectiveOptions = {
  bind(el: HTMLElement, binding, vnode) {
    const options: ColumnResizeOptions = binding.value || {};
    const minWidth = options.minWidth || 50;
    const maxWidth = options.maxWidth || 500;
    const storageKey = options.storageKey || 'tariff_column_widths';
    
    // Создаем уникальный ID для колонки если не предоставлен
    const columnId = options.columnId || 
                    el.getAttribute('data-column-id') || 
                    `column_${Math.random().toString(36).substr(2, 9)}`;
    
    // Сохраняем ID в элементе
    el.setAttribute('data-column-id', columnId);
    
    // Функция для нахождения всех элементов с таким же columnId
    const findAllColumnElements = (): HTMLElement[] => {
      const elements: HTMLElement[] = [];
      const allElements = document.querySelectorAll('[data-column-id]');
      
      allElements.forEach((element: Element) => {
        if ((element as HTMLElement).getAttribute('data-column-id') === columnId) {
          elements.push(element as HTMLElement);
        }
      });
      
      return elements;
    };
    
    // Функция для применения ширины ко всем элементам колонки
    const applyWidthToAllColumnElements = (width: number) => {
      const allElements = findAllColumnElements();
      allElements.forEach(element => {
        element.style.width = `${width}px`;
        element.style.minWidth = `${width}px`;
      });
    };
    
    // Загружаем сохраненную ширину
    const loadSavedWidth = () => {
      const savedWidths = JSON.parse(localStorage.getItem(storageKey) || '{}');
      const savedWidth = savedWidths[columnId];
      
      if (savedWidth) {
        applyWidthToAllColumnElements(savedWidth);
      }
    };
    
    // Загружаем сохраненную ширину при инициализации
    loadSavedWidth();
    
    // Создаем элемент для захвата resize
    const resizeHandle = document.createElement('div');
    resizeHandle.style.position = 'absolute';
    resizeHandle.style.right = '-2px';
    resizeHandle.style.top = '0';
    resizeHandle.style.width = '4px';
    resizeHandle.style.height = '100%';
    resizeHandle.style.cursor = 'col-resize';
    resizeHandle.style.zIndex = '1000';
    resizeHandle.style.backgroundColor = 'transparent';
    
    el.appendChild(resizeHandle);
    
    let isResizing = false;
    let startX = 0;
    let startWidth = 0;
    
    const startResize = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      isResizing = true;
      startX = e.clientX;
      startWidth = el.offsetWidth;
      
      document.addEventListener('mousemove', resize);
      document.addEventListener('mouseup', stopResize);
      
      // Добавляем класс для визуальной обратной связи
      el.classList.add('resizing');
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    };
    
    const resize = (e: MouseEvent) => {
      if (!isResizing) return;
      
      const deltaX = e.clientX - startX;
      const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + deltaX));
      
      // Обновляем ширину ВСЕХ элементов этой колонки
      applyWidthToAllColumnElements(newWidth);
    };
    
    const stopResize = () => {
      if (!isResizing) return;
      
      isResizing = false;
      el.classList.remove('resizing');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', stopResize);
      
      // Сохраняем ширину в localStorage
      const currentWidth = el.offsetWidth;
      const savedWidths = JSON.parse(localStorage.getItem(storageKey) || '{}');
      savedWidths[columnId] = currentWidth;
      localStorage.setItem(storageKey, JSON.stringify(savedWidths));
      
      // Вызываем callback если предоставлен
      if (options.onResize) {
        options.onResize(currentWidth, columnId);
      }
    };
    
    // Добавляем обработчики событий
    resizeHandle.addEventListener('mousedown', startResize);
    
    // Сохраняем ссылки на функции для очистки
    (el as any)._columnResizeHandlers = {
      resizeHandle,
      startResize,
      resize,
      stopResize,
      findAllColumnElements,
      applyWidthToAllColumnElements
    };
  },
  
  unbind(el: HTMLElement) {
    const handlers = (el as any)._columnResizeHandlers;
    if (handlers) {
      handlers.resizeHandle.removeEventListener('mousedown', handlers.startResize);
      document.removeEventListener('mousemove', handlers.resize);
      document.removeEventListener('mouseup', handlers.stopResize);
      
      if (handlers.resizeHandle.parentNode) {
        handlers.resizeHandle.parentNode.removeChild(handlers.resizeHandle);
      }
    }
  }
}